import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

import { DEFAULT_RESULTS_PAGE_SIZE } from "./results/useInfiniteResultsPageSize";
import {
  prefetchMeasurementResultsMetadata,
  prefetchSpirometryResultsMetadata,
} from "./results/resultsMetadataPrefetch";
import { RESULT_MODE_CONFIG } from "../utils/results/resultModeConfig";
import { RECORDING_MODES } from "../utils/recordingModes";

/**
 * Warms the most likely post-login read paths once the backend-owned account
 * context has resolved.
 *
 * Questionnaire access is already fully determined by `/v1/users/me`; this
 * hook waits for that same bootstrap to settle and then primes result-list
 * metadata so the first visit to results feels immediate.
 */
export default function useRegionalBootstrapWarmup({
  db,
  uid,
  ready = false,
  recordingAccessError = null,
  permittedModes = [],
  permittedQuestionnaires = [],
}) {
  const warmedKeyRef = useRef("");
  const { i18n } = useTranslation();

  useEffect(() => {
    if (!ready || !db || !uid || recordingAccessError) {
      return undefined;
    }

    const language = i18n.resolvedLanguage || i18n.language || "en-GB";
    const warmupKey = [
      uid,
      language,
      [...permittedModes].sort().join(","),
      [...permittedQuestionnaires].sort().join(","),
    ].join("|");

    if (warmedKeyRef.current === warmupKey) {
      return undefined;
    }

    warmedKeyRef.current = warmupKey;

    const timerId = window.setTimeout(() => {
      const tasks = [];

      if (permittedModes.includes(RECORDING_MODES.SPIROMETRY)) {
        tasks.push(
          prefetchSpirometryResultsMetadata({
            db,
            uid,
            pageSize: DEFAULT_RESULTS_PAGE_SIZE,
            language,
          }),
        );
      }

      if (permittedModes.includes(RECORDING_MODES.TIDAL)) {
        tasks.push(
          prefetchMeasurementResultsMetadata({
            db,
            uid,
            collectionName: RESULT_MODE_CONFIG.cardiopulmonary.collectionName,
            pageSize: DEFAULT_RESULTS_PAGE_SIZE,
            language,
          }),
        );
      }

      if (permittedModes.includes(RECORDING_MODES.MAUDIO)) {
        tasks.push(
          prefetchMeasurementResultsMetadata({
            db,
            uid,
            collectionName: RESULT_MODE_CONFIG.manualAudio.collectionName,
            orderField: "createdAt",
            pageSize: DEFAULT_RESULTS_PAGE_SIZE,
            language,
          }),
        );
      }

      if (permittedModes.includes(RECORDING_MODES.POSTEXERTION)) {
        tasks.push(
          prefetchMeasurementResultsMetadata({
            db,
            uid,
            collectionName: RESULT_MODE_CONFIG.postExertion.collectionName,
            orderField: "createdAt",
            pageSize: DEFAULT_RESULTS_PAGE_SIZE,
            language,
          }),
        );
      }

      if (permittedModes.includes(RECORDING_MODES.SIX_MINUTE_WALK)) {
        tasks.push(
          prefetchMeasurementResultsMetadata({
            db,
            uid,
            collectionName: RESULT_MODE_CONFIG.sixMinuteWalk.collectionName,
            orderField: "createdAt",
            pageSize: DEFAULT_RESULTS_PAGE_SIZE,
            language,
          }),
        );
      }

      Promise.allSettled(tasks).catch(() => {});
    }, 0);

    return () => {
      window.clearTimeout(timerId);
    };
  }, [
    db,
    i18n.language,
    i18n.resolvedLanguage,
    permittedModes,
    permittedQuestionnaires,
    ready,
    recordingAccessError,
    uid,
  ]);
}
